/**
 * First-party attribution capture + server-side event forwarding.
 *
 * This is the client half of the "Approach 2" tracking router. It:
 *   1. Reads ad click-ids / UTMs from the current URL.
 *   2. Merges them into a persistent first-party cookie (`_first_party_attribution`,
 *      30-day, SameSite=Lax) WITHOUT clobbering previously-captured values on
 *      internal navigation.
 *   3. Exposes `sendServerEvent(eventName, customData)` which fires a
 *      non-blocking request to our backend `/track-event` proxy.
 *
 * It intentionally does NOT touch GA4 / GTM — those remain owned by
 * `src/lib/analytics.ts`. This layer is additive and complementary.
 *
 * CONSENT RULES (do not relax these without legal sign-off)
 * --------------------------------------------------------
 * Writing to the attribution cookie is storage on the visitor's device, so it
 * requires consent in the EEA/UK under ePrivacy Art. 5(3). Therefore:
 *
 *   - Campaign parameters are ALWAYS parsed into memory. Holding them for the
 *     life of the page stores nothing on the device and needs no consent.
 *   - The cookie is written ONLY once consent is granted. If consent is granted
 *     later in the page's life, the in-memory touch is flushed at that moment.
 *   - If consent is denied or absent, nothing is persisted and any pre-existing
 *     cookie is deleted. The in-memory value is simply discarded on unload.
 *   - No event is forwarded to the backend without consent.
 *
 * Never persist "pending" consent — buffering attribution in storage while
 * waiting for a yes is precisely the storage the law prohibits.
 *
 * Consent today is a single `analytics` category covering our own measurement.
 * When a separate `marketing` category is introduced, any forwarding of click
 * identifiers onward to an ad platform must additionally gate on that category;
 * analytics consent alone does not authorise advertising use.
 */

import { COOKIE_CONSENT_UPDATED_EVENT, readCookieConsent } from './cookieConsent';

const COOKIE_NAME = '_first_party_attribution';
const COOKIE_MAX_AGE_DAYS = 30;
const COOKIE_MAX_AGE_SECONDS = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;

/** Ad click-ids + UTM params we capture. */
const TRACKED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'li_fat_id', // LinkedIn
  'fbclid', // Meta / Facebook
  'gclid', // Google Ads
  'ttclid', // TikTok
  'msclkid', // Microsoft / Bing
  'rdt_cid', // Reddit
] as const;

export type TrackedParam = (typeof TRACKED_PARAMS)[number];

export interface AttributionData extends Partial<Record<TrackedParam, string>> {
  /** URL of the first page where attribution was captured. */
  landingPage?: string;
  /** document.referrer at first capture. */
  referrer?: string;
  /** ISO timestamp of first capture (first-touch). */
  firstTouchAt?: string;
  /** ISO timestamp of most recent capture (last-touch). */
  lastTouchAt?: string;
}

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
const TRACK_ENDPOINT = `${API_BASE}/track-event`;
const DEBUG = import.meta.env.VITE_GA_DEBUG_MODE?.trim().toLowerCase() === 'true';
const USER_STORAGE_KEY = 'caloriescience_user';

const debug = (...args: unknown[]) => {
  if (DEBUG) console.debug('[attribution]', ...args);
};

/* ------------------------------------------------------------------ consent */

/**
 * Whether we may persist attribution and forward events.
 *
 * Mirrors the gate used by `src/lib/analytics.ts`: absence of a stored record
 * means no decision has been made yet, which the law treats the same as a
 * refusal. Only an explicit `true` grants permission.
 */
export const isAttributionAllowed = (): boolean =>
  isBrowser() && readCookieConsent()?.analytics === true;

/**
 * The current page's campaign parameters, held in memory only.
 *
 * This is populated on every page load regardless of consent — holding values
 * in a module variable stores nothing on the device. It is flushed to the
 * cookie if and when consent is granted, and otherwise dies with the page.
 */
let pendingTouch: AttributionData | null = null;

/* ------------------------------------------------------------------ cookies */

export function readAttributionCookie(): AttributionData | null {
  if (!isBrowser()) return null;
  const entry = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!entry) return null;
  try {
    const raw = decodeURIComponent(entry.substring(COOKIE_NAME.length + 1));
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as AttributionData) : null;
  } catch {
    return null;
  }
}

function writeAttributionCookie(data: AttributionData): void {
  if (!isBrowser()) return;
  const value = encodeURIComponent(JSON.stringify(data));
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${value}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

/**
 * Delete the attribution cookie on both the exact host and the parent domain.
 *
 * Called when consent is refused or withdrawn. The parent-domain pass matters
 * because a cookie set on `.caloriescience.ai` is not removed by expiring it on
 * the exact host alone.
 */
export function clearAttributionCookie(): void {
  if (!isBrowser()) return;
  const expire = 'Max-Age=0; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `${COOKIE_NAME}=; ${expire}`;
  const host = window.location.hostname;
  const parent = host.split('.').slice(-2).join('.');
  if (parent && parent !== host) {
    document.cookie = `${COOKIE_NAME}=; ${expire}; Domain=.${parent}`;
  }
  debug('cleared attribution cookie');
}

/* ---------------------------------------------------------------- capturing */

type ParamSource = URLSearchParams | Record<string, string | null | undefined>;

function getParam(source: ParamSource, key: string): string | undefined {
  const value = source instanceof URLSearchParams ? source.get(key) : source[key];
  if (value === null || value === undefined) return undefined;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : undefined;
}

/**
 * Merge current URL params into the attribution record.
 *
 * Resilience rule: a param that is absent/empty in the current URL NEVER
 * overwrites a previously-saved value. This keeps campaign attribution intact
 * when the user navigates internal links (which have no UTMs).
 *
 * Consent rule: the merged record is always returned and always retained in
 * memory, but it is only written to the cookie when consent has been granted.
 * Without consent the cookie is left untouched — and any cookie surviving from
 * an earlier grant is removed, so a withdrawal takes effect immediately.
 */
export function captureAttributionFromParams(source: ParamSource): AttributionData {
  const allowed = isAttributionAllowed();

  // Without consent we must not read a persisted record either; base the merge
  // on whatever we already hold in memory for this page instead.
  const existing = (allowed ? readAttributionCookie() : pendingTouch) || {};
  const merged: AttributionData = { ...existing };

  let hasNewSignal = false;
  for (const key of TRACKED_PARAMS) {
    const incoming = getParam(source, key);
    if (incoming !== undefined) {
      merged[key] = incoming;
      hasNewSignal = true;
    }
  }

  const nowIso = new Date().toISOString();

  // First-touch metadata is written once and preserved.
  if (!merged.firstTouchAt) {
    merged.firstTouchAt = nowIso;
    if (isBrowser()) {
      merged.landingPage = merged.landingPage || window.location.href;
      merged.referrer = merged.referrer || (document.referrer || undefined);
    }
  }

  // Always keep the latest view in memory; this is what gets flushed if the
  // visitor consents later in this page's life.
  pendingTouch = merged;

  if (!allowed) {
    // No consent: persist nothing, and drop anything left over from before.
    if (readAttributionCookie()) clearAttributionCookie();
    debug('captured in memory only (no consent)', merged);
    return merged;
  }

  // Only persist when we actually captured a new campaign signal or when no
  // cookie existed yet — avoids rewriting the cookie on every internal route
  // change while still establishing first-touch on the very first visit.
  const hadCookie = Object.keys(existing).length > 0;
  if (hasNewSignal || !hadCookie) {
    merged.lastTouchAt = nowIso;
    writeAttributionCookie(merged);
    debug('captured', merged);
  }

  return merged;
}

/**
 * Persist the in-memory touch once consent arrives, or clear on withdrawal.
 *
 * Bound to the consent-updated event so a visitor who accepts after landing
 * still gets correct first-touch attribution, without us having stored anything
 * while we waited for the answer.
 */
export function syncAttributionWithConsent(): void {
  if (!isBrowser()) return;

  if (!isAttributionAllowed()) {
    pendingTouch = null;
    clearAttributionCookie();
    return;
  }

  if (!pendingTouch) return;

  const existing = readAttributionCookie() || {};
  const merged: AttributionData = { ...pendingTouch, ...existing };
  // Preserve the earliest first-touch across both sources.
  if (pendingTouch.firstTouchAt && existing.firstTouchAt) {
    merged.firstTouchAt =
      pendingTouch.firstTouchAt < existing.firstTouchAt
        ? pendingTouch.firstTouchAt
        : existing.firstTouchAt;
    if (merged.firstTouchAt === pendingTouch.firstTouchAt) {
      merged.landingPage = pendingTouch.landingPage;
      merged.referrer = pendingTouch.referrer;
    }
  }
  merged.lastTouchAt = new Date().toISOString();
  writeAttributionCookie(merged);
  debug('flushed pending attribution after consent', merged);
}

/* ---------------------------------------------------------- server events */

function newEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function readStoredBearerToken(): string | null {
  if (!isBrowser()) return null;

  try {
    const rawUser = window.localStorage.getItem(USER_STORAGE_KEY);
    if (!rawUser) return null;

    const parsed = JSON.parse(rawUser) as { token?: unknown };
    return typeof parsed.token === 'string' && parsed.token ? parsed.token : null;
  } catch {
    return null;
  }
}

export interface SendServerEventOptions {
  /** Shared id so the vendor can de-dupe this against the browser pixel. */
  eventId?: string;
}

/**
 * Fire a non-blocking conversion event to our server-side proxy.
 *
 * Uses `fetch` with `keepalive` so it survives page navigation/unload, and
 * falls back to `navigator.sendBeacon`. Never throws and never blocks the
 * caller — tracking must not affect UX.
 *
 * Does nothing without consent. Identity is deliberately NOT accepted here:
 * the backend already knows the authenticated user and can derive and hash any
 * identifier it needs server-side. Sending an email or phone number from the
 * browser would put readable personal data on the wire for no benefit, and a
 * hash of an email is still personal data rather than an anonymous token.
 */
export function sendServerEvent(
  eventName: string,
  customData: Record<string, unknown> = {},
  options: SendServerEventOptions = {}
): void {
  if (!isBrowser() || !API_BASE) {
    debug('skipped sendServerEvent (no window or VITE_API_URL)', eventName);
    return;
  }

  if (!isAttributionAllowed()) {
    debug('skipped sendServerEvent (no consent)', eventName);
    return;
  }

  const payload = {
    eventName,
    eventId: options.eventId || newEventId(),
    url: window.location.href,
    attribution: readAttributionCookie(),
    customData,
  };

  const body = JSON.stringify(payload);
  const bearerToken = readStoredBearerToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (bearerToken) {
    headers.Authorization = `Bearer ${bearerToken}`;
  }

  try {
    if (typeof fetch === 'function') {
      void fetch(TRACK_ENDPOINT, {
        method: 'POST',
        headers,
        body,
        keepalive: true,
      }).catch((err) => debug('sendServerEvent fetch error', err));
      return;
    }

    if (navigator && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(TRACK_ENDPOINT, new Blob([body], { type: 'application/json' }));
    }
  } catch (err) {
    debug('sendServerEvent failed', err);
  }
}

// Make the helper reachable from anywhere (non-React modules, console, GTM).
declare global {
  interface Window {
    sendServerEvent?: typeof sendServerEvent;
  }
}

if (isBrowser()) {
  window.sendServerEvent = sendServerEvent;
  // React to the visitor accepting, changing or withdrawing consent: flush the
  // in-memory touch on a grant, and delete the cookie on a refusal.
  window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, syncAttributionWithConsent);
}
